import React, { useCallback, useMemo, useState } from 'react';

import { Box, Button, Container, Typography } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { generatePath, useHistory, useLocation } from 'react-router-dom';

import ErrorBoundary from 'components/ErrorBoundary';
import LiveRegionStatus from 'components/LiveRegionStatus';
import Loader from 'components/Loader';
import ManagePageHeader from 'components/ManagePageHeader/ManagePageHeader';
import TextButton from 'components/TextButton/TextButton';
import assetRopaApi from 'config/api/assetRopa/assetRopa';
import { isAvailableProcessingActivityGoneError } from 'config/api/assetRopa/assetRopaWorkspace.helpers';
import MUTATION_KEYS from 'config/api/MUTATION_KEYS';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import ropaReviewsApi from 'config/api/ropaReviews/ropaReviews';
import type { AssetProcessingActivityReviewRow, AssetRopaReview } from 'config/api/ropaReviews/ropaReviews.types';
import usePageTitle from 'hooks/usePageTitle';
import general_messages from 'messages/general_messages';
import processing_activities_messages from 'messages/processing_activities_messages';
import StartRopaReviewModal from 'pages/ProcessingActivities/_components/StartRopaReviewModal/StartRopaReviewModal';
import DpiaReassessDialog from 'pages/ProcessingActivities/_shared/DpiaReassessDialog';
import useDpiaReassess from 'pages/ProcessingActivities/_shared/useDpiaReassess';
import { useConfirmationModalContext } from 'reactContext/ConfirmationModalContext/ConfirmationModalContext';
import PATHS from 'router/PATHS';
import userDataStorage from 'storages/userDataStorage';

import { resolveAssetRopaCapabilities, type AssetRopaPageVariant } from '../_shared/assetRopaCapabilities';
import { canAccessAdminAssetRopaPage } from '../_shared/assetRopaReviewAccess';
import { isReviewLocked } from '../_shared/assetRopaReviewHelpers';
import RopaReviewClosureBanner from '../_shared/RopaReviewClosureBanner/RopaReviewClosureBanner';
import AddProcessingActivityModal from '../OwnerAssetRopaWorkspacePage/_components/AddProcessingActivityModal';
import SoftDeleteProcessingActivityModal from '../OwnerAssetRopaWorkspacePage/_components/SoftDeleteProcessingActivityModal';

import ActiveRopaReviewPanelSection from './_components/ActiveRopaReviewPanelSection';
import AssetRopaExpandedAssetSection from './_components/AssetRopaExpandedAssetSection';
import AssetRopaFiltersToolbar from './_components/AssetRopaFiltersToolbar';
import AssetRopaMetricsSection from './_components/AssetRopaMetricsSection';
import AssetRopaReviewsTable, { type AssetRopaAssetMenuAction } from './_components/AssetRopaReviewsTable';
import CancelRopaReviewModal from './_components/CancelRopaReviewModal';
import CompleteRopaReviewModal from './_components/CompleteRopaReviewModal';
import EditRopaReviewModal from './_components/EditRopaReviewModal';
import RequestClarificationModal from './_components/RequestClarificationModal';
import { DEFAULT_ASSET_ROPA_REVIEW_FILTERS } from './assetRopaReview.types';
import { getSubmittedReviewCount } from './assetRopaReviewHelpers';
import { resolveAssetRopaReviewId } from './mapOrganizationWorkspaceAssetToReview';
import useStyles from './ProcessingActivitiesAssetReviewsPage.styles';
import useAssetRopaReviewPage from './useAssetRopaReviewPage';

type Props = {
  variant?: AssetRopaPageVariant;
};

type LocationState = {
  fromProcessingActivities?: boolean;
};

type ClarificationTarget =
  | { type: 'row'; row: AssetProcessingActivityReviewRow }
  | { type: 'asset'; rows: AssetProcessingActivityReviewRow[] };

const getClarifiableRows = (asset: AssetRopaReview) =>
  asset.processingActivityReviews.filter(row => row.canRequestClarification || row.reviewStatus === 'submitted');

const AssetRopaReviewPage: React.FC<Props> = ({ variant = 'admin' }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { enqueueSnackbar } = useSnackbar();
  const { showConfirmationModal } = useConfirmationModalContext();
  const userData = userDataStorage.get() || {};
  const organizationId = typeof userData.organization === 'number' ? userData.organization : undefined;
  const isAdminVariant = variant === 'admin';
  const canAccessPage = !isAdminVariant || canAccessAdminAssetRopaPage(userData);
  const showBackToProcessingActivities = isAdminVariant && Boolean(location.state?.fromProcessingActivities);

  const [filters, setFilters] = useState(DEFAULT_ASSET_ROPA_REVIEW_FILTERS);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedAssetId, setExpandedAssetId] = useState<number | null>(null);
  const [startReviewModalOpen, setStartReviewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [addTarget, setAddTarget] = useState<AssetRopaReview | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    asset: AssetRopaReview;
    row: AssetProcessingActivityReviewRow;
  } | null>(null);
  const [clarificationTarget, setClarificationTarget] = useState<ClarificationTarget | null>(null);
  const [cancelIncompleteTarget, setCancelIncompleteTarget] = useState<AssetRopaReview | null>(null);
  const [reassessTarget, setReassessTarget] = useState<AssetProcessingActivityReviewRow | null>(null);
  const reassessMutation = useDpiaReassess(organizationId);

  const {
    acceptProcessingReviewMutation,
    activeReview,
    addProcessingActivityMutation,
    allAssetReviews,
    bulkAcceptMutation,
    cancelIncompleteAssetReviewMutation,
    cancelReviewMutation,
    completeReviewMutation,
    confirmNoProcessingActivitiesMutation,
    filterOptions,
    filteredAssetReviews,
    invalidateReviewQueries,
    isError,
    isForbidden,
    isLoading,
    refetch,
    requestClarificationMutation,
    softDeleteMutation,
    updateReviewDetailsMutation,
    workspaceMetrics,
  } = useAssetRopaReviewPage({ enabled: canAccessPage, filters: { ...filters, search: searchTerm }, organizationId, variant });

  const startSummaryQuery = useQuery(
    [QUERY_KEYS.ROPA_REVIEWS_START_SUMMARY, organizationId],
    async () => {
      const { data } = await ropaReviewsApi.getStartSummary(organizationId as number);
      return data;
    },
    {
      enabled: Boolean(organizationId && isAdminVariant && !activeReview && !isLoading),
      retry: false,
    },
  );
  const startSummary = startSummaryQuery.data;

  const createReviewMutation = useMutation(
    MUTATION_KEYS.ROPA_REVIEWS_CREATE,
    async (payload: { deadline: string; reviewInstructions: string }) => {
      const { data } = await ropaReviewsApi.createReview(organizationId as number, payload);
      return data;
    },
    {
      onSuccess: invalidateReviewQueries,
    },
  );

  const capabilities = useMemo(
    () => resolveAssetRopaCapabilities(variant, activeReview?.review.status),
    [activeReview?.review.status, variant],
  );

  const pageMessages = processing_activities_messages.asset_ropa;

  usePageTitle(t(pageMessages[capabilities.pageTitleMessageKey]));

  const searchStatusMessage = useMemo(() => {
    if (!canAccessPage || isForbidden) {
      return '';
    }

    if (isLoading) {
      return t(pageMessages.search_status.loading);
    }

    if (filteredAssetReviews.length === 0 && !activeReview) {
      return t(pageMessages.empty_state.no_inventory_title);
    }

    if (filteredAssetReviews.length === 0) {
      return `${t(pageMessages.empty_state.no_assets_title)}. ${t(pageMessages.empty_state.no_assets_body)}`;
    }

    return t(pageMessages.search_status.assets_count, { count: filteredAssetReviews.length });
  }, [activeReview, canAccessPage, filteredAssetReviews.length, isForbidden, isLoading, pageMessages, t]);

  const handleAcceptRow = useCallback(
    async (row: AssetProcessingActivityReviewRow) => {
      const confirmed = await showConfirmationModal({
        title: t(pageMessages.actions.accept),
        body: row.processingActivityName,
      });

      if (!confirmed) return;

      try {
        await acceptProcessingReviewMutation.mutateAsync(row.id);
        enqueueSnackbar(t(pageMessages.toasts.review_accepted), { variant: 'success' });
      } catch {
        enqueueSnackbar(t(pageMessages.toasts.action_failed), { variant: 'error' });
      }
    },
    [acceptProcessingReviewMutation, enqueueSnackbar, pageMessages, showConfirmationModal, t],
  );

  const handleRequestClarification = useCallback((row: AssetProcessingActivityReviewRow) => {
    setClarificationTarget({ type: 'row', row });
  }, []);

  const handleClarificationConfirm = useCallback(
    async (comment: string) => {
      if (!clarificationTarget) return;

      const reviewRowIds = clarificationTarget.type === 'row' ? [clarificationTarget.row.id] : clarificationTarget.rows.map(row => row.id);

      try {
        await Promise.all(reviewRowIds.map(reviewRowId => requestClarificationMutation.mutateAsync({ comment, reviewRowId })));
        enqueueSnackbar(t(pageMessages.toasts.clarification_requested), { variant: 'success' });
        setClarificationTarget(null);
      } catch {
        enqueueSnackbar(t(pageMessages.toasts.action_failed), { variant: 'error' });
      }
    },
    [clarificationTarget, enqueueSnackbar, pageMessages, requestClarificationMutation, t],
  );

  const handleBulkAccept = useCallback(
    async (asset: AssetRopaReview) => {
      const submittedCount = getSubmittedReviewCount(asset);
      const confirmed = await showConfirmationModal({
        title: t(pageMessages.bulk_accept.confirm_title),
        body: t(pageMessages.bulk_accept.confirm_body, { count: submittedCount }),
      });

      if (!confirmed) return;

      const assetReviewId = resolveAssetRopaReviewId(asset);
      if (assetReviewId == null) {
        enqueueSnackbar(t(pageMessages.toasts.action_failed), { variant: 'error' });
        return;
      }

      try {
        await bulkAcceptMutation.mutateAsync(assetReviewId);
        enqueueSnackbar(t(pageMessages.toasts.bulk_accepted), { variant: 'success' });
      } catch {
        enqueueSnackbar(t(pageMessages.toasts.action_failed), { variant: 'error' });
      }
    },
    [bulkAcceptMutation, enqueueSnackbar, pageMessages, showConfirmationModal, t],
  );

  const handleStartReviewConfirm = useCallback(
    async (payload: { deadline: string; reviewInstructions: string }) => {
      try {
        await createReviewMutation.mutateAsync(payload);
        enqueueSnackbar(t(pageMessages.toasts.review_started), { variant: 'success' });
        setStartReviewModalOpen(false);
      } catch {
        enqueueSnackbar(t(pageMessages.toasts.start_review_failed), { variant: 'error' });
      }
    },
    [createReviewMutation, enqueueSnackbar, pageMessages, t],
  );

  const handleEditReviewConfirm = useCallback(
    async (payload: { deadline: string; reviewInstructions: string }) => {
      try {
        await updateReviewDetailsMutation.mutateAsync(payload);
        enqueueSnackbar(t(pageMessages.toasts.review_updated), { variant: 'success' });
        setEditModalOpen(false);
      } catch {
        enqueueSnackbar(t(pageMessages.toasts.action_failed), { variant: 'error' });
      }
    },
    [enqueueSnackbar, pageMessages, t, updateReviewDetailsMutation],
  );

  const handleCompleteReviewConfirm = useCallback(
    async (comment: string) => {
      const metrics = activeReview?.metrics;
      const incompleteCount =
        (metrics?.awaitingResponseCount ?? 0) + (metrics?.submittedCount ?? 0) + (metrics?.clarificationRequestedCount ?? 0);

      if (incompleteCount > 0) {
        const confirmed = await showConfirmationModal({
          title: t(pageMessages.modals.complete_review.incomplete_confirm_title),
          body: t(pageMessages.modals.complete_review.incomplete_confirm_body, { count: incompleteCount }),
        });

        if (!confirmed) return;
      }

      try {
        await completeReviewMutation.mutateAsync(comment);
        enqueueSnackbar(t(pageMessages.toasts.review_completed), { variant: 'success' });
        setCompleteModalOpen(false);
      } catch {
        enqueueSnackbar(t(pageMessages.toasts.action_failed), { variant: 'error' });
      }
    },
    [activeReview?.metrics, completeReviewMutation, enqueueSnackbar, pageMessages, showConfirmationModal, t],
  );

  const handleCancelReviewConfirm = useCallback(
    async (comment: string) => {
      try {
        await cancelReviewMutation.mutateAsync(comment);
        enqueueSnackbar(t(pageMessages.toasts.review_cancelled), { variant: 'success' });
        setCancelModalOpen(false);
      } catch {
        enqueueSnackbar(t(pageMessages.toasts.action_failed), { variant: 'error' });
      }
    },
    [cancelReviewMutation, enqueueSnackbar, pageMessages, t],
  );

  const handleOpenAdminReview = useCallback(
    (row: AssetProcessingActivityReviewRow) => {
      history.push(generatePath(PATHS.PROCESSING_ACTIVITIES_ASSET_REVIEW_APA, { reviewRowId: String(row.id) }));
    },
    [history],
  );

  const handleOpenOwnerEditor = useCallback(
    (row: AssetProcessingActivityReviewRow, asset: AssetRopaReview, mode: 'view' | 'edit') => {
      history.push(
        generatePath(PATHS.PROCESSING_ACTIVITIES_ASSET_ROPA_APA, {
          assetRopaId: String(asset.assetRopaId),
          apaId: String(row.assetProcessingActivityId),
        }),
        { mode },
      );
    },
    [history],
  );

  const handleAssessDpia = useCallback(
    (row: AssetProcessingActivityReviewRow) => {
      if (row.dpiaDisplayStatus === 'approved') {
        setReassessTarget(row);
        return;
      }

      history.push({
        pathname: generatePath(PATHS.PROCESSING_ACTIVITIES_DPIA_ASSESSMENT, {
          apaId: String(row.assetProcessingActivityId),
        }),
        state: {
          returnPath: history.location.pathname,
          sourceAssetProcessingActivityReviewId: row.id,
          sourceRopaReviewRoundId: activeReview?.review.id,
          dpiaId: row.dpiaId ?? undefined,
        },
      });
    },
    [activeReview?.review.id, history],
  );

  const handleReassessConfirm = useCallback(
    async (reason: string) => {
      if (!reassessTarget) return;

      try {
        const currentId = await reassessMutation.mutateAsync({
          apaId: reassessTarget.assetProcessingActivityId,
          reason,
        });
        enqueueSnackbar(t(pageMessages.toasts.reassessed), { variant: 'success' });
        setReassessTarget(null);
        history.push(
          generatePath(PATHS.PROCESSING_ACTIVITIES_ADMIN_DPIA_REVIEW, {
            dpiaId: String(currentId),
          }),
          { returnPath: history.location.pathname },
        );
      } catch {
        enqueueSnackbar(t(pageMessages.toasts.reassess_failed), { variant: 'error' });
      }
    },
    [enqueueSnackbar, history, pageMessages.toasts, reassessMutation, reassessTarget, t],
  );

  const handleOpenAdminDpiaReview = useCallback(
    (row: AssetProcessingActivityReviewRow) => {
      if (!row.dpiaId) return;

      history.push(
        generatePath(PATHS.PROCESSING_ACTIVITIES_ADMIN_DPIA_REVIEW, {
          dpiaId: String(row.dpiaId),
        }),
        { returnPath: history.location.pathname },
      );
    },
    [history],
  );

  const handleAddConfirm = useCallback(
    async (processingActivityId: number) => {
      if (!addTarget) return 'failed';

      try {
        await addProcessingActivityMutation.mutateAsync({
          assetRopaId: addTarget.assetRopaId,
          processingActivityId,
        });
        enqueueSnackbar(t(pageMessages.toasts.processing_activity_added), { variant: 'success' });
        setAddTarget(null);
        setExpandedAssetId(addTarget.id);
        return 'success' as const;
      } catch (error) {
        if (isAvailableProcessingActivityGoneError(error)) {
          return 'unavailable' as const;
        }

        enqueueSnackbar(t(pageMessages.toasts.action_failed), { variant: 'error' });
        return 'failed' as const;
      }
    },
    [addProcessingActivityMutation, addTarget, enqueueSnackbar, pageMessages, t],
  );

  const handleRemoveProcessingActivity = useCallback((row: AssetProcessingActivityReviewRow, asset: AssetRopaReview) => {
    setDeleteTarget({ asset, row });
  }, []);

  const handleDeleteConfirm = useCallback(
    async (reason: string) => {
      if (!deleteTarget) return;

      try {
        await softDeleteMutation.mutateAsync({
          apaId: deleteTarget.row.assetProcessingActivityId,
          assetRopaId: deleteTarget.asset.assetRopaId,
          payload: { deletionReason: reason },
        });
        enqueueSnackbar(t(pageMessages.owner_workspace.toasts.removed), { variant: 'success' });
        setDeleteTarget(null);
      } catch {
        enqueueSnackbar(t(pageMessages.owner_workspace.toasts.remove_failed), { variant: 'error' });
      }
    },
    [deleteTarget, enqueueSnackbar, pageMessages, softDeleteMutation, t],
  );

  const handleConfirmNoneApply = useCallback(
    async (asset: AssetRopaReview) => {
      const confirmed = await showConfirmationModal({
        title: t(pageMessages.actions.confirm_none_apply),
        body: t(pageMessages.panel.no_processing_activities_body),
      });

      if (!confirmed) return;

      try {
        await confirmNoProcessingActivitiesMutation.mutateAsync(asset.assetRopaId);
        enqueueSnackbar(t(pageMessages.toasts.confirm_none_apply), { variant: 'success' });
      } catch {
        enqueueSnackbar(t(pageMessages.toasts.action_failed), { variant: 'error' });
      }
    },
    [confirmNoProcessingActivitiesMutation, enqueueSnackbar, pageMessages, showConfirmationModal, t],
  );

  const handleCancelIncompleteConfirm = useCallback(
    async (comment: string) => {
      if (!cancelIncompleteTarget) return;

      try {
        const assetReviewId = resolveAssetRopaReviewId(cancelIncompleteTarget);
        if (assetReviewId == null) {
          enqueueSnackbar(t(pageMessages.toasts.action_failed), { variant: 'error' });
          return;
        }

        await cancelIncompleteAssetReviewMutation.mutateAsync({
          assetReviewId,
          cancellationComment: comment,
        });
        enqueueSnackbar(t(pageMessages.toasts.asset_review_cancelled), { variant: 'success' });
        setCancelIncompleteTarget(null);
        if (expandedAssetId === cancelIncompleteTarget.id) {
          setExpandedAssetId(null);
        }
      } catch {
        enqueueSnackbar(t(pageMessages.toasts.action_failed), { variant: 'error' });
      }
    },
    [cancelIncompleteAssetReviewMutation, cancelIncompleteTarget, enqueueSnackbar, expandedAssetId, pageMessages, t],
  );

  const handleAssetMenuAction = useCallback(
    (asset: AssetRopaReview, action: AssetRopaAssetMenuAction) => {
      if (action === 'open_asset_ropa') {
        setExpandedAssetId(asset.id);
        return;
      }

      if (action === 'accept_all_submitted') {
        handleBulkAccept(asset);
        return;
      }

      if (action === 'cancel_incomplete_asset_review') {
        setCancelIncompleteTarget(asset);
        return;
      }

      if (action === 'request_clarification') {
        const rows = getClarifiableRows(asset);
        if (rows.length === 0) {
          enqueueSnackbar(t(pageMessages.toasts.clarification_none_eligible), { variant: 'info' });
          return;
        }

        setClarificationTarget({ type: 'asset', rows });
      }
    },
    [enqueueSnackbar, handleBulkAccept, pageMessages, t],
  );

  const reviewLocked = activeReview ? isReviewLocked(activeReview.review.status) : false;
  const panelActions = reviewLocked
    ? { canEditDetails: false, canCompleteReview: false, canCancelReview: false }
    : activeReview?.actions ?? { canEditDetails: false, canCompleteReview: false, canCancelReview: false };

  const handleExpandAsset = useCallback((asset: AssetRopaReview) => {
    setExpandedAssetId(currentId => (currentId === asset.id ? null : asset.id));
  }, []);

  const expandedListAsset = useMemo(
    () => filteredAssetReviews.find(asset => asset.id === expandedAssetId) ?? null,
    [expandedAssetId, filteredAssetReviews],
  );

  const expandedAssetLiveMessage = useMemo(() => {
    if (!expandedListAsset) {
      return '';
    }

    return t(pageMessages.search_status.expanded_asset, { name: expandedListAsset.assetName });
  }, [expandedListAsset, pageMessages.search_status.expanded_asset, t]);

  const ownerMineDetailQuery = useQuery(
    [QUERY_KEYS.ASSET_ROPA_MINE_DETAIL, organizationId, expandedListAsset?.assetRopaId],
    async () => {
      const { data } = await assetRopaApi.getMineDetail(organizationId as number, expandedListAsset!.assetRopaId);
      return data;
    },
    {
      enabled: Boolean(!isAdminVariant && organizationId && expandedListAsset?.assetRopaId),
      retry: false,
    },
  );

  const renderExpandedAsset = useCallback(
    (asset: AssetRopaReview) => {
      const detailAsset =
        !isAdminVariant && ownerMineDetailQuery.data && ownerMineDetailQuery.data.assetRopaId === asset.assetRopaId
          ? {
              ...asset,
              processingActivityReviews:
                asset.processingActivityReviews.length > 0
                  ? asset.processingActivityReviews
                  : ownerMineDetailQuery.data.processingActivityReviews,
            }
          : asset;

      return (
        <AssetRopaExpandedAssetSection
          asset={detailAsset as AssetRopaReview}
          bulkAcceptLoading={bulkAcceptMutation.isLoading}
          canConfirmNoneApply={
            !reviewLocked && !detailAsset.warnings.confirmedNoProcessingActivities && capabilities.canAddProcessingActivity
          }
          capabilities={capabilities}
          onAcceptRow={handleAcceptRow}
          onAddProcessingActivity={setAddTarget}
          onAssessDpia={handleAssessDpia}
          onBulkAccept={handleBulkAccept}
          onConfirmNoneApply={handleConfirmNoneApply}
          onOpenAdminDpiaReview={handleOpenAdminDpiaReview}
          onOpenAdminReview={handleOpenAdminReview}
          onOpenOwnerEditor={handleOpenOwnerEditor}
          onRemoveProcessingActivity={handleRemoveProcessingActivity}
          onRequestClarification={handleRequestClarification}
          reviewLocked={reviewLocked}
        />
      );
    },
    [
      bulkAcceptMutation.isLoading,
      capabilities,
      handleAcceptRow,
      handleAssessDpia,
      handleBulkAccept,
      handleConfirmNoneApply,
      handleOpenAdminDpiaReview,
      handleOpenAdminReview,
      handleOpenOwnerEditor,
      handleRemoveProcessingActivity,
      handleRequestClarification,
      isAdminVariant,
      ownerMineDetailQuery.data,
      reviewLocked,
    ],
  );

  let content: React.ReactNode;

  if (!canAccessPage || isForbidden) {
    content = (
      <Box className={styles.forbiddenState}>
        <Typography gutterBottom variant='h6'>
          {t(pageMessages.forbidden_title)}
        </Typography>
        <Typography color='textSecondary' variant='body2'>
          {t(isAdminVariant ? pageMessages.forbidden_body : pageMessages.owner_forbidden_body)}
        </Typography>
        <Button color='primary' onClick={() => history.push(PATHS.ROOT)} style={{ marginTop: 16 }} variant='contained'>
          {t(processing_activities_messages.forbidden_back)}
        </Button>
      </Box>
    );
  } else if (isLoading) {
    content = <Loader inner />;
  } else if (isError) {
    content = (
      <Box className={styles.errorBanner}>
        <Typography color='error' gutterBottom variant='body2'>
          {t(pageMessages.errors.load_failed)}
        </Typography>
        <TextButton onClick={() => refetch()}>{t(general_messages.retry)}</TextButton>
      </Box>
    );
  } else {
    const hasInventory = allAssetReviews.length > 0;
    const showIdleStartCta = isAdminVariant && !activeReview;

    content = (
      <>
        {activeReview && <RopaReviewClosureBanner review={activeReview.review} />}
        {activeReview && !reviewLocked && (
          <ActiveRopaReviewPanelSection
            actions={panelActions}
            onCancelReview={() => setCancelModalOpen(true)}
            onCompleteReview={() => setCompleteModalOpen(true)}
            onEditReview={() => setEditModalOpen(true)}
            review={activeReview.review}
            showAdminActions={capabilities.reviewPanelAdminActions}
          />
        )}
        {showIdleStartCta && (
          <Box className={styles.idleNote}>
            <Typography color='textSecondary' variant='body2'>
              {t(pageMessages.empty_state.no_active_review_body)}
              {startSummary
                ? ` ${t(pageMessages.empty_state.eligible_assets_summary, {
                    assets: startSummary.eligibleAssetsCount,
                    owners: startSummary.uniqueOperationalOwnersCount,
                    apas: startSummary.assetProcessingActivitiesCount,
                  })}`
                : ''}
            </Typography>
          </Box>
        )}
        <AssetRopaMetricsSection
          includedAssetsMessageKey={capabilities.metricsIncludedAssetsMessageKey}
          metrics={activeReview?.metrics ?? undefined}
          workspaceMetrics={activeReview ? undefined : workspaceMetrics}
        />
        <AssetRopaFiltersToolbar
          filterOptions={filterOptions}
          filters={filters}
          onFiltersChange={setFilters}
          onSearch={setSearchTerm}
          searchValue={searchTerm}
        />
        <AssetRopaReviewsTable
          assets={filteredAssetReviews}
          emptyStateContent={
            <>
              <Typography gutterBottom variant='h6'>
                {t(hasInventory ? pageMessages.empty_state.no_assets_title : pageMessages.empty_state.no_inventory_title)}
              </Typography>
              <Typography color='textSecondary' variant='body2'>
                {t(hasInventory ? pageMessages.empty_state.no_assets_body : pageMessages.empty_state.no_inventory_body)}
              </Typography>
            </>
          }
          expandedAssetId={expandedAssetId}
          hasActiveReview={Boolean(activeReview)}
          onAssetMenuAction={handleAssetMenuAction}
          onExpandAsset={handleExpandAsset}
          renderExpandedAsset={renderExpandedAsset}
          showAdminAssetMenu={Boolean(activeReview && capabilities.showAdminAssetMenu)}
        />
      </>
    );
  }

  return (
    <ErrorBoundary>
      <Container>
        <ManagePageHeader
          backButton={showBackToProcessingActivities}
          noBorder
          onBack={() => history.push(PATHS.PROCESSING_ACTIVITIES)}
          rightAdornment={
            isAdminVariant && !activeReview && !isLoading && canAccessPage && !isForbidden ? (
              <Box className={styles.headerActions}>
                <Button onClick={() => setStartReviewModalOpen(true)} variant='outlined'>
                  {t(pageMessages.actions.start_review)}
                </Button>
              </Box>
            ) : undefined
          }
          subtitle={t(pageMessages[capabilities.pageSubtitleMessageKey])}
        >
          {t(pageMessages[capabilities.pageTitleMessageKey])}
        </ManagePageHeader>
        <LiveRegionStatus message={searchStatusMessage} />
        <LiveRegionStatus message={expandedAssetLiveMessage} />
        {content}
        {isAdminVariant && (
          <StartRopaReviewModal
            loading={createReviewMutation.isLoading}
            onClose={() => setStartReviewModalOpen(false)}
            onConfirm={handleStartReviewConfirm}
            open={startReviewModalOpen}
            summary={startSummary}
            summaryLoading={startSummaryQuery.isLoading}
          />
        )}
        {activeReview && capabilities.canEditReviewDetails && (
          <EditRopaReviewModal
            deadline={activeReview.review.deadline}
            instructions={activeReview.review.reviewInstructions}
            loading={updateReviewDetailsMutation.isLoading}
            onClose={() => setEditModalOpen(false)}
            onConfirm={handleEditReviewConfirm}
            open={editModalOpen}
          />
        )}
        {activeReview && capabilities.canCompleteReview && (
          <CompleteRopaReviewModal
            loading={completeReviewMutation.isLoading}
            metrics={activeReview.metrics ?? undefined}
            onClose={() => setCompleteModalOpen(false)}
            onConfirm={handleCompleteReviewConfirm}
            open={completeModalOpen}
          />
        )}
        {activeReview && capabilities.canCancelReview && (
          <CancelRopaReviewModal
            loading={cancelReviewMutation.isLoading}
            metrics={activeReview.metrics ?? undefined}
            onClose={() => setCancelModalOpen(false)}
            onConfirm={handleCancelReviewConfirm}
            open={cancelModalOpen}
          />
        )}
        {capabilities.canRequestClarificationFromList && (
          <RequestClarificationModal
            loading={requestClarificationMutation.isLoading}
            onClose={() => setClarificationTarget(null)}
            onConfirm={handleClarificationConfirm}
            open={Boolean(clarificationTarget)}
          />
        )}
        {cancelIncompleteTarget && (
          <RequestClarificationModal
            confirmLabel={pageMessages.modals.cancel_incomplete_asset_review.confirm}
            helpLabel={pageMessages.modals.cancel_incomplete_asset_review.help}
            loading={cancelIncompleteAssetReviewMutation.isLoading}
            onClose={() => setCancelIncompleteTarget(null)}
            onConfirm={handleCancelIncompleteConfirm}
            open
            titleLabel={pageMessages.modals.cancel_incomplete_asset_review.title}
          />
        )}
        <DpiaReassessDialog
          loading={reassessMutation.isLoading}
          onClose={() => setReassessTarget(null)}
          onConfirm={handleReassessConfirm}
          open={Boolean(reassessTarget)}
        />
        {addTarget && (
          <AddProcessingActivityModal
            assetRopaId={addTarget.assetRopaId}
            loading={addProcessingActivityMutation.isLoading}
            onClose={() => setAddTarget(null)}
            onConfirm={handleAddConfirm}
            open
            organizationId={organizationId}
          />
        )}
        <SoftDeleteProcessingActivityModal
          loading={softDeleteMutation.isLoading}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          open={Boolean(deleteTarget)}
          processingActivityName={deleteTarget?.row.processingActivityName}
        />
      </Container>
    </ErrorBoundary>
  );
};

export default AssetRopaReviewPage;
